import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel"; // 조회조건 Panel
import { fnGetDataSet, fnSave } from "@shared/api/restApi"; // BackEnd API
import {
  getCodeData,
  getInitFormData,
  getNoSlip,
  getTablebySql,
  applySlip,
} from "@shared/utils/CommonUtils"; // 공통 유틸 메뉴 초기화(조회조건, 그리드, 프리폼) 설정 load 관련 내용
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global } from "@shared/libs/Global"; // Login User관련 전역 변수( 로그인 정보)
import SnGrid from "@shared/components/GridV2/SnGrid"; // 공통 그리드
import { menuInfo } from "@shared/type/CommonType"; // 공통 Interface
import { showPopupModal, showResizablePopupModal } from "@widgets/CommonModal";
import SnButton from "@shared/components/SnButton";
import SnResizeBar from "@shared/components/SnResizeBar";
import SN_PRD_MRE_REG_001_POP_01 from "../SN_PRD_MRE_REG_001/SN_PRD_MRE_REG_001_POP_01";
import SN_PRD_MRE_REG_001_POP_02 from "./SN_PRD_MRE_REG_001_POP_02";
import { RowNode } from "ag-grid-community";

interface Props {
  Menuprops: menuInfo;
}

interface RowData {
  KeyField?: string; // 행의 고유 ID
  [key: string]: any;
}

const SN_PRD_MRE_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
  });
  const searchRef = useRef<any>(null);
  const today = new Date();
  const todayform = `${today.getFullYear()}${(today.getMonth() + 1)
    .toString()
    .padStart(2, "0")}${today.getDate().toString().padStart(2, "0")}`;
  const { userInfo, config } = global(); // Login user 정보
  //#endregion

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
  });

  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData({});
        const PRD1103 = config.PRD1103?.dc_ctrl;

        let updatedFormData = { ...rtn.formData };

        if (PRD1103 && !isNaN(Number(PRD1103))) {
          const days = Number(PRD1103);

          if (updatedFormData.dt_end) {
            const dt = updatedFormData.dt_end;
            const date = new Date(
              dt.replace(/(\d{4})(\d{2})(\d{2})/, "$1-$2-$3")
            );
            date.setDate(date.getDate() + days);

            updatedFormData.dt_end = date
              .toISOString()
              .slice(0, 10)
              .replace(/-/g, "");
          }
        }

        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        setFormData(updatedFormData);
        //setFormData(rtn.formData); // 조회조건 초기값
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  //#region  Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  const onSearch = async () => {
    const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
    const modifiedRowsL = gridRefs.current["grid2"].current.getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        await onSave();
      }
    }
    try {
      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();

      const selectedFormData = searchRef.current.getFormdata();
      const selectedData = {
        cd_biz: selectedFormData.cd_biz,
        cd_cust: selectedFormData.cd_cust,
        dt_from: selectedFormData.dt_from,
        dt_to: selectedFormData.dt_to,
      };
      const component = <SN_PRD_MRE_REG_001_POP_01 />;
      const result = await showPopupModal({
        title: "소요량 조회",
        children: component,
        validation: true,
        width: "50%",
      });

      if (!result || result === "close") return;
      const cdBiz = result[0][0].cd_biz;
      const noMre = result[0][0].no_mre;

      const params = {
        id: "sn_prd_mre_reg_001_01",
        params: {
          ...searchRef.current.getFormdata(),
          cd_biz: cdBiz,
          no_mre: noMre,
        },
      };

      const data: any = await fnGetDataSet(params);
      const header = data?.[0]?.[0] || {};
      setFormData((prev: any) => ({
        cd_biz: cdBiz,
        dt: header.dt,
        no_mre: noMre,
        yn_partial: header.fg_exp === "01" ? "Y" : "N",
        check1: header.yn_inv,
        check2: header.yn_ss,
        check3: header.yn_isu_p,
        check4: header.yn_rcv_p,
        cd_emp: header.cd_emp,
        nm_emp: header.nm_emp,
        dt_start: header.dt_start,
        dt_end: header.dt_end,
        detail: header.dc_detail,
        cd_wh: header.cd_wh_search,
      }));
      setDataSources({
        grid1: data[1],
        grid2: data[2],
      });
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  const onInsert = async () => {
    try {
      const rtn: any = await getInitFormData({});
      const PRD1103 = config.PRD1103?.dc_ctrl;

      let updatedFormData = { ...rtn.formData };

      if (PRD1103 && !isNaN(Number(PRD1103))) {
        const days = Number(PRD1103);

        if (updatedFormData.dt_end) {
          const dt = updatedFormData.dt_end;
          const date = new Date(
            dt.replace(/(\d{4})(\d{2})(\d{2})/, "$1-$2-$3")
          );
          date.setDate(date.getDate() + days);

          updatedFormData.dt_end = date
            .toISOString()
            .slice(0, 10)
            .replace(/-/g, "");
        }
      }

      setFormControlData(rtn.formControlData);
      setFormData(updatedFormData);
      setColums({
        grid1: rtn.grid.grid1,
        grid2: rtn.grid.grid2,
      });
      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      setDataSources({
        grid1: [],
        grid2: [],
      });
    } catch (error) {}
  };

  const onDelete = async () => {
    if (gridRefs.current["grid1"].current?.getDisplayedRowCount() < 1) return;

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");
    if (!confirm) return;

    let isApproved = false;

    gridRefs.current["grid2"].current?.forEachFilteredNode((node: any) => {
      if (node.data?.yn_app === "Y") {
        isApproved = true;
      }
    });
    if (isApproved) {
      await showDialog("alert", "승인된 데이터입니다. 승인취소 후 진행하세요.");
      return;
    }

    gridRefs.current["grid2"].current.deleteAllRows();
    gridRefs.current["grid1"].current.deleteAllRows();

    const SaveData = {
      cd_firm: userInfo?.cd_firm,
      no_mre: searchRef.current.getFormdata()?.no_mre,
    };

    await fnSave("SN_PRD_MRP_REG_001_DELETE_01", SaveData);

    gridRefs.current["grid1"].current.resetUpdate();
    gridRefs.current["grid2"].current.resetUpdate();

    const rtn: any = await getInitFormData({});
    const PRD1103 = config.PRD1103?.dc_ctrl;

    let updatedFormData = { ...rtn.formData };

    if (PRD1103 && !isNaN(Number(PRD1103))) {
      const days = Number(PRD1103);

      if (updatedFormData.dt_end) {
        const dt = updatedFormData.dt_end;
        const date = new Date(dt.replace(/(\d{4})(\d{2})(\d{2})/, "$1-$2-$3"));
        date.setDate(date.getDate() + days);

        updatedFormData.dt_end = date
          .toISOString()
          .slice(0, 10)
          .replace(/-/g, "");
      }
    }

    setFormControlData(rtn.formControlData); // 조회조건 항목 설정
    setFormData(updatedFormData);
  };

  const onSave = async () => {
    try {
      gridRefs.current["grid2"].current.stopEditing();

      const requiredValuesCheckL =
        gridRefs.current["grid2"].current.requiredValuesCheck();
      if (!requiredValuesCheckL) return false;

      const lChanges = gridRefs.current["grid2"].current.getChange();

      const lineInserts = lChanges.insert ?? [];

      if (lChanges.modifiedRowcount === 0) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const lineChanges = [
        ...(lChanges.insert ?? []),
        ...(lChanges.update ?? []),
      ];

      const saveData = {
        dtMREREQ: lChanges,
      };
      const ok = await fnSave("SN_PRD_MRP_REG_001_SAVE_02", saveData);
      if (!ok) return false;

      gridRefs.current["grid2"].current.resetUpdate();
      gridRefs.current["grid1"].current.resetUpdate();

      return true;
    } catch (error: any) {
      gridRefs.current["grid2"].current.resetUpdate(true);
      gridRefs.current["grid1"].current.resetUpdate(true);
      return false;
    }
  };
  //#endregion

  //#region  Event Method

  const Grid1btnclick = async (id: string) => {
    try {
      const selectedFormData = searchRef.current.getFormdata();
      if (id === "aButtonApply") {
        const str_dt = selectedFormData?.dt;
        const str_dtS = selectedFormData?.dt_start;
        const str_dtE = selectedFormData?.dt_end;
        const selectedData = {
          cd_biz: selectedFormData.cd_biz,
          dt_from: selectedFormData.dt_from,
          dt_to: selectedFormData.dt_to,
        };

        const component = (
          <SN_PRD_MRE_REG_001_POP_02 selectedData={selectedData} />
        );
        const result = await showResizablePopupModal({
          title: "전개",
          children: component,
          validation: true,
        });
        if (!result || result === "close") return;

        const returnTable = result?.[0] || [];
        if (returnTable.length === 0) return;

        const no_slip = await getNoSlip(
          "PRD",
          15,
          "",
          str_dt,
          Menuprops.cd_menu
        );

        const convertedRows = returnTable.map((row: any) => ({
          ...row,
          dt: str_dt,
          no_mre: no_slip,
          dt_start: row.dt_start || str_dtS,
          dt_end: row.dt_end || str_dt,
        }));

        const baseRow = {
          cd_firm: selectedFormData.cd_firm,
          cd_biz: selectedData.cd_biz,
          dt: str_dt,
          no_mre: no_slip,
          st_confirm: "N",
          yn_inv: selectedFormData.check1,
          yn_ss: selectedFormData.check2,
          yn_isu_p: selectedFormData.check3,
          yn_rcv_p: selectedFormData.check4,
          fg_exp: selectedFormData.yn_partial === "Y" ? "01" : "02",
          fg_bom: "001",
          dt_start: selectedFormData.dt_start,
          dt_end: selectedFormData.dt_end,
          cd_dept: userInfo?.cd_dept,
          cd_emp: userInfo?.cd_emp,
          dc_detail: selectedFormData.detail,
          cd_wh_search: selectedFormData.cd_wh,
        };

        const sqlQuery = {
          cd_firm: userInfo.cd_firm,
          noMre: no_slip,
          yn_partial: selectedFormData.yn_partial,
        };

        const SaveData = {
          dtMREBASE: {
            insert: [baseRow],
            update: [],
            delete: [],
          },
          dtMREINPUT: {
            insert: convertedRows,
            update: [],
            delete: [],
          },
          dtMREREQ: sqlQuery,
        };
        // 저장 API 호출
        const ok = await fnSave("SN_PRD_MRP_REG_001_SAVE_01", SaveData);
        if (!ok) return false;

        gridRefs.current["grid1"].current.resetUpdate();
        gridRefs.current["grid2"].current.resetUpdate();

        const params = {
          id: "sn_prd_mre_reg_001_01",
          params: {
            ...selectedFormData,
            cd_biz: selectedFormData.cd_biz,
            no_mre: no_slip,
            dt: todayform,
            cd_emp: userInfo?.cd_emp,
            nm_emp: userInfo?.nm_emp,
          },
        };
        setFormData((prev: any) => ({
          ...selectedFormData,
          no_mre: no_slip,
        }));
        const data: any = await fnGetDataSet(params);
        setDataSources({
          grid1: data[1],
          grid2: data[2],
        });
      } else if (id === "fn_app") {
        const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
        const focusedNodeL = gridRefs.current["grid2"].current.focusedRowNode();
        const displayedNodes: any[] = [];
        let hasInvalid = false;

        const checkedRows =
          gridRefs.current["grid2"].current.getCheckedRows() || [];
        const checkedKeys = new Set(
          checkedRows.map((row: any) => row.KeyField)
        );

        gridRefs.current["grid2"].current.forEachFilteredNode(
          (node: RowNode) => {
            // 26.04.23 kdy 남상도선임님 요청
            if (
              checkedKeys.has(node.data.KeyField) &&
              node.data.yn_app === "N"
            ) {
              displayedNodes.push(node);
              hasInvalid = true;
            }
          }
        );

        if (!hasInvalid) {
          await showDialog("alter", "선택된 미승인 건이 없습니다.");
          return false;
        }

        const items = displayedNodes.map((node) => node.data);

        const payload = {
          cd_firm: userInfo?.cd_firm,
          cd_dept: userInfo?.cd_dept,
          cd_emp: userInfo?.cd_emp,
          cd_gp: userInfo?.cd_gp,
          userId: userInfo?.cd_user,

          dt: selectedFormData.dt,
          cd_biz: selectedFormData.cd_biz,

          no_mre: focusedNodeH.data.no_mre,
          cd_pjt: focusedNodeH.data.cd_pjt || "",

          noPrq: "",
          noPrd: "",
          noSct: "",

          items,
        };

        const ok = await fnSave(
          "SN_PRD_MRP_REG_001_APPROVE",
          payload,
          "APPROVE"
        );
        if (!ok) return false;

        gridRefs.current["grid1"].current.resetUpdate();
        gridRefs.current["grid2"].current.resetUpdate();

        const params = {
          id: "sn_prd_mre_reg_001_01",
          params: {
            ...selectedFormData,
            cd_biz: selectedFormData.cd_biz,
            no_mre: focusedNodeH.data.no_mre,
            dt: todayform,
          },
        };
        const data: any = await fnGetDataSet(params);
        setDataSources({
          grid1: data[1],
          grid2: data[2],
        });
      } else if (id === "fn_appcancel") {
        const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
        const focusedNodeL = gridRefs.current["grid2"].current.focusedRowNode();
        const displayedNodes: any[] = [];
        let hasInvalid = false;

        const checkedRows =
          gridRefs.current["grid2"].current.getCheckedRows() || [];
        const checkedKeys = new Set(
          checkedRows.map((row: any) => row.KeyField)
        );

        gridRefs.current["grid2"].current.forEachFilteredNode(
          (node: RowNode) => {
            // 26.04.23 kdy 남상도선임님 요청
            if (
              checkedKeys.has(node.data.KeyField) &&
              node.data.yn_app === "Y"
            ) {
              displayedNodes.push(node);
              hasInvalid = true;
            }
          }
        );

        if (!hasInvalid) {
          await showDialog("alter", "선택된 승인 건이 없습니다.");
          return false;
        }

        const items = displayedNodes.map((node) => node.data);

        const payload = {
          cd_firm: userInfo?.cd_firm,
          cd_dept: userInfo?.cd_dept,
          cd_emp: userInfo?.cd_emp,
          cd_gp: userInfo?.cd_gp,
          userId: userInfo?.cd_user,

          dt: selectedFormData.dt,
          cd_biz: selectedFormData.cd_biz,

          no_mre: focusedNodeH.data.no_mre,

          items,
        };

        const ok = await fnSave(
          "SN_PRD_MRP_REG_001_CANCEL",
          payload,
          "APPROVE_CANCEL"
        );
        if (!ok) return false;

        gridRefs.current["grid1"].current.resetUpdate();
        gridRefs.current["grid2"].current.resetUpdate();

        const params = {
          id: "sn_prd_mre_reg_001_01",
          params: {
            ...selectedFormData,
            cd_biz: selectedFormData.cd_biz,
            no_mre: focusedNodeH.data.no_mre,
            dt: todayform,
          },
        };
        const data: any = await fnGetDataSet(params);
        setDataSources({
          grid1: data[1],
          grid2: data[2],
        });
      }
    } catch (error: any) {
      gridRefs.current["grid1"].current.resetUpdate(true);
      gridRefs.current["grid2"].current.resetUpdate(true);
    }
  };

  const onFocusedRowChanged = (event: any) => {
    const selectedRow = event;
    if (selectedRow) {
      const no_mre = selectedRow.no_mre;
      gridRefs.current["grid2"].current.applyFilters({ no_mre: no_mre });
      gridRefs.current["grid2"].current.selectAndFocusRow(0);
    } else {
      return;
    }
  };

  //#endregion

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onFocusedRowChanged,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
      },
    }),
    [columns, dataSources]
  );

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]} // Grid Name
            columnConfig={grids.grid1.columns} // 컬럼 설정 전달
            rowData={grids.grid1.dataSource} // Datasource
            etcProps={{ rowSelection: "single", title: "의뢰정보" }}
            events={grids.grid1.events}
            header={
              <div className="relative flex space-x-3">
                <SnButton
                  text="전개"
                  onClick={() => {
                    Grid1btnclick("aButtonApply");
                  }}
                />
                <SnButton
                  text="승인"
                  onClick={() => {
                    Grid1btnclick("fn_app");
                  }}
                />
                <SnButton
                  text="승인취소"
                  onClick={() => {
                    Grid1btnclick("fn_appcancel");
                  }}
                />
              </div>
            }
          />
        </SnResizeBar>

        <SnResizeBar direction={"height"} id="panel2" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid2"]} // Grid Name
            columnConfig={grids.grid2.columns} // 컬럼 설정 전달
            rowData={grids.grid2.dataSource} // Datasource
            etcProps={{
              rowSelection: "multiple",
              title: "상세정보",
              ischeckboxSelection: true,
            }}
          />
        </SnResizeBar>
      </div>
    </div>
  );
  //#endregion
});

export default SN_PRD_MRE_REG_001;
